import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { RowsLeadingSummary } from './types/rows-leading-summary'

/**
 * Trailing marker on an explicit ABSOLUTE leading whose magnitude is `<= LEADING_RATIO_BAND_MAX`
 * (including 0) — the one case magnitude alone can't disambiguate from a legacy ratio. Old
 * documents, and every write site that predates this marker, never produced it, so its absence
 * still falls back to the original magnitude-only rule; this is purely additive. See
 * `parseStoredLeading`.
 *
 * Exported so the one write site that needs to produce it — `toStoredLeadingString`
 * (`type-panel/helpers/leading-presets.ts`) — stays byte-for-byte in sync with the reader here,
 * rather than
 * duplicating the literal.
 */
export const EXPLICIT_ABSOLUTE_LEADING_SUFFIX = 'pt'

/**
 * Parses a stored leading string into its numeric value and whether it's a ratio (a multiplier of
 * point size) or an absolute pt length — the one place both the value AND that distinction are
 * resolved together. `null` for "Metric"/absent/invalid, matching `storedLeadingNumber`.
 *
 * Magnitude alone (`<= LEADING_RATIO_BAND_MAX` → ratio) is enough for everything ABOVE the band,
 * and for anything legacy: a real yb360 document can genuinely carry a small ratio like `"1.2"`,
 * so a bare number in the band must keep reading as one. But a value explicitly typed as an
 * absolute pt length in [0, LEADING_RATIO_BAND_MAX] — e.g. Leading set to `0` — has no bare-number
 * encoding that survives a reload: clamping such values up out of the band (an earlier approach)
 * is what let the input/slider and the applied result disagree (a typed `0` silently becoming
 * `5.1`). The `EXPLICIT_ABSOLUTE_LEADING_SUFFIX` marker carries that intent through the wire
 * instead: `"0pt"` always reads as an absolute 0, never a ratio, while a bare `"0"` (nothing
 * writes this today, but a document could) still reads as absent, matching the pre-existing rule.
 *
 * Exported so every reader of a stored leading string — including the Type panel's own
 * selection-resync display logic (`type-panel/hooks/use-leading-field.ts`) — goes through this ONE parser rather than hand-rolling a
 * `parseFloat` + magnitude check that doesn't know about the marker and silently miscategorizes an
 * explicit `"0pt"`/`"2pt"` as a legacy ratio again.
 */
export function parseStoredLeading(
  leading: string | undefined | null
): { n: number; isRatio: boolean; isExplicitAbsolute: boolean } | null {
  if (!leading || leading === 'Metric') return null

  const trimmed = leading.trim()
  const isExplicitAbsolute = trimmed.toLowerCase().endsWith(EXPLICIT_ABSOLUTE_LEADING_SUFFIX)
  const numericPart = isExplicitAbsolute
    ? trimmed.slice(0, trimmed.length - EXPLICIT_ABSOLUTE_LEADING_SUFFIX.length)
    : trimmed
  const n = parseFloat(numericPart)

  if (!isFinite(n)) return null
  // A bare (non-suffixed) 0 or negative number stays "absent", matching the original rule — only
  // the explicit-suffix form can express a real, applied 0.
  if (n < 0 || (n === 0 && !isExplicitAbsolute)) return null

  return { n, isRatio: !isExplicitAbsolute && isLeadingRatio(n), isExplicitAbsolute }
}

/**
 * The stored leading as a finite non-negative number, or `null` for "Metric"/absent/invalid.
 * Only the magnitude — the ratio-vs-absolute decision belongs to `parseStoredLeading`.
 */
function storedLeadingNumber(leading: string | undefined | null): number | null {
  return parseStoredLeading(leading)?.n ?? null
}

/**
 * Whether a stored leading is a real value someone chose, as opposed to a default standing in for
 * one.
 *
 * The single definition of "explicit", so the sentinel vocabulary cannot drift between the readers.
 * `resolveRowLeadingRun` (`lib/shapes/shape-text.ts`) used to test `!== undefined && !== 'Metric'`
 * by hand, which missed `DEFAULT_LEADING` (`'auto'`) — the value `ensureTextRunDefaults`
 * (`stowed-element-builder.ts`) stamps on every run that has no leading of its own, so it is present
 * on everything after a single save. That let a stamped default outrank a value typed into part of
 * the same row, which is exactly the defect `resolveRowLeadingRun` exists to prevent.
 *
 * Deliberately delegates to `storedLeadingNumber`: anything that resolver treats as absent —
 * `Metric`, `auto`, empty, non-numeric, non-positive (unless explicitly marked absolute) — is
 * implicit here too, by construction. A stored `0` DOES count as explicit here (it's a real,
 * applied value, not a default standing in for one), which is why it must go through
 * `EXPLICIT_ABSOLUTE_LEADING_SUFFIX` rather than being read as plain absence.
 */
export function hasExplicitLeading(leading: string | undefined | null): boolean {
  return storedLeadingNumber(leading) != null
}

/**
 * Legacy ratio values are ≤5; absolute pt values are >5. The one place that MAGNITUDE-ONLY rule is
 * expressed — used by write sites computing a fresh number with no wire string yet
 * (`cssLineHeightToStoredLeading`) and by `parseStoredLeading`
 * itself for the bare (non-suffixed) case. A read site that already has the stored STRING should
 * go through `parseStoredLeading` instead, which also honours `EXPLICIT_ABSOLUTE_LEADING_SUFFIX`.
 *
 * The boundary is undefended by construction — anything that WRITES a stored leading must either
 * keep its value out of the ratio band or mark it with `EXPLICIT_ABSOLUTE_LEADING_SUFFIX`, or a
 * small absolute value is silently reinterpreted as a huge multiplier (`4` meaning "4pt" becomes
 * "4× the point size"). `type-panel/helpers/leading-presets.ts` guards its three generators with
 * `LEADING_RATIO_BAND_MAX` for exactly this reason.
 */
function isLeadingRatio(n: number): boolean {
  return n <= LEADING_RATIO_BAND_MAX
}

/** Largest stored leading value still read as a ratio rather than an absolute pt length, absent
 *  `EXPLICIT_ABSOLUTE_LEADING_SUFFIX`. */
export const LEADING_RATIO_BAND_MAX = 5

/**
 * Foreign 96dpi CSS px → document points.
 *
 * Exported so the clipboard parser converts `font-size` and `line-height` through the SAME factor.
 * They disagreed before — only `font-size` converted — which is what made a pasted heading's line
 * gap 4/3 too loose (`cssLineHeightToStoredLeading`).
 */
export const FOREIGN_CSS_PX_TO_PT = 0.75

/** `normal` plus the CSS-wide keywords: every one of them means "no explicit leading" here. */
const CSS_LINE_HEIGHT_KEYWORDS = new Set([
  'normal',
  'inherit',
  'initial',
  'unset',
  'revert',
  'revert-layer',
])

/** Trims binary-float artefacts so a converted leading stores as `1.15`, not `1.1500000000000001` —
 *  the stored value is a string, so the artefact would otherwise be permanent. */
function roundLeading(value: number, places: number): number {
  const factor = 10 ** places

  return Math.round(value * factor) / factor
}

/**
 * Smallest row pitch, as a fraction of the natural height, that `parsedLeadingPt` will honour.
 *
 * This is a damage bound, **not** a no-overlap guarantee — deliberately. Tight leading is a real
 * typographic choice (the Narrow preset is sub-natural at every point size, and display type is
 * routinely set with leading below the font size), so a floor high enough to prevent all ink overlap
 * would break legitimate settings. `0.5` sits far below every value the app itself can produce —
 * Narrow's tightest across the font-size presets is `0.79 ×` natural, at 8pt — while still refusing
 * the pathological ratios that come from a leading authored against a much smaller point size
 * (12.4pt leading on 72pt text is `0.14 ×`, which stacked rows on top of each other).
 *
 * Applies to the **absolute** branch only — see `parsedLeadingPt`.
 *
 * A Narrow/Wide preset is kept in step with the point size by `restateLeadingPresetForPointSize`
 * (`lib/text/leading-presets`); a typed value stays fixed in points by design (WMPCM-9899), so this
 * floor is what bounds it when the point size later grows far past it.
 */
export const MIN_LEADING_FRACTION_OF_NATURAL = 0.5

/** The font's assumed natural (un-leaded) line height multiplier — the `1.2×` this app already uses
 *  for "Metric" leading and the Narrow/Wide presets (`computeNarrowLeadingPt`/`computeWideLeadingPt`
 *  in `type-panel/helpers/leading-presets.ts`). No real per-font metrics are available without measuring the specific
 *  font file, so every leading calculation in this app shares this one approximation for consistency
 *  (including shape text frames — see `use-shape-text-layout.ts`). */
export const NATURAL_LEADING_MULTIPLIER = 1.2

/** The font's assumed natural (un-leaded) line height in pt. */
export function naturalLeadingPt(pointSize: number): number {
  return pointSize * NATURAL_LEADING_MULTIPLIER
}

/** A stored leading value ("Metric", a legacy ≤5 ratio, or an absolute pt string — see
 *  `parseStoredLeading`) resolved to a concrete pt number — "Metric"/absent resolves to the
 *  natural height itself (zero delta). */
export function parsedLeadingPt(leading: string | undefined | null, pointSize: number): number {
  const parsed = parseStoredLeading(leading)
  const naturalPt = naturalLeadingPt(pointSize)

  if (!parsed) return naturalPt

  // A ratio is size-relative by definition, so it CANNOT fall out of step with the point size and is
  // returned exactly as stored. Deliberately not floored: `parseStoredLeading` accepts any bare
  // value ≤5 as a ratio, wire leading is passed through unvalidated (`checkpoint-to-text.ts`), and
  // flooring here would silently relayout an existing yb360 document that legitimately stores a
  // tight ratio.
  if (parsed.isRatio) return parsed.n * pointSize

  // An EXPLICIT-marked absolute value (`isExplicitAbsolute` — see `EXPLICIT_ABSOLUTE_LEADING_SUFFIX`)
  // is a Leading value the user set directly against the CURRENT point size, in the current
  // session — it cannot have drifted, because nothing but this one write path
  // (`toStoredLeadingString` in `lib/text/leading-presets`) has ever produced this marker, and a
  // typed value stays fixed in points when the point size changes (WMPCM-9899). So it's returned
  // exactly as typed, including a literal 0 — matching what the Type panel's input/slider show,
  // with nothing left to disagree.
  if (parsed.isExplicitAbsolute) return parsed.n

  // See MIN_LEADING_FRACTION_OF_NATURAL. Only a BARE (non-suffixed) absolute leading needs this: it
  // is meaningful only against the point size it was authored for, and when the two drift apart —
  // e.g. a value loaded from an old document, or from before this rescale/marker mechanism existed —
  // it can resolve to a pitch far below one line and collapse rows into each other (WMPCM-9070 Bug 6).
  return Math.max(parsed.n, naturalPt * MIN_LEADING_FRACTION_OF_NATURAL)
}

/** A run's leading as the Type panel compares it: a size-relative value (Metric/absent, or a legacy
 *  ratio) in points at the run's own size, a typed absolute value as typed. Deliberately not
 *  `parsedLeadingPt`, whose overlap floor would split one typed value across point sizes. */
function comparableLeadingPt(leading: string | null | undefined, pointSize: number): number {
  const parsed = parseStoredLeading(leading)

  if (!parsed) return naturalLeadingPt(pointSize)

  return parsed.isRatio ? parsed.n * pointSize : parsed.n
}

type LeadingRun = { leading?: string | null; pointSize?: number | null }

/**
 * The Leading of a set of visual rows, compared row by row. A row's value is its last explicit
 * leading (`resolveRowLeadingRun`), else Metric at its largest point size, so 7pt beside 24pt text on
 * one Metric row reads 28.8, not Mixed. Only two rows that space differently are Mixed.
 */
export function summarizeRowsLeading(rows: LeadingRun[][]): RowsLeadingSummary | null {
  let first: { pt: number; leading: string | undefined } | undefined
  let mixed = false
  let largestPointSize = 0

  for (const runs of rows) {
    if (runs.length === 0) continue

    const explicit = runs.filter((run) => hasExplicitLeading(run.leading)).at(-1)
    const rowPointSize = Math.max(...runs.map((run) => run.pointSize ?? DEFAULT_POINT_SIZE))
    const pt = explicit
      ? comparableLeadingPt(explicit.leading, explicit.pointSize ?? DEFAULT_POINT_SIZE)
      : naturalLeadingPt(rowPointSize)

    largestPointSize = Math.max(largestPointSize, rowPointSize)

    if (!first) first = { pt, leading: explicit?.leading ?? undefined }
    else if (pt !== first.pt) mixed = true
  }

  if (!first) return null

  return { mixed, leading: first.leading, pointSize: largestPointSize }
}

/**
 * A foreign CSS `line-height` restated in the stored leading format — the inverse of
 * `parsedLeadingPt`, for text arriving from outside the app (a web page, Word, Google Docs).
 *
 * Pasted markup is 96dpi CSS, so an absolute length needs the same px→pt conversion
 * `parseElementPointSize` already applies to `font-size` (`parse-external-clipboard-html.ts`).
 * Storing the raw CSS string instead — which the paste path did until WMPCM-9070 — read
 * `line-height: 41.6px` as 41.6 **pt** of leading on 24pt text (natural is 28.8) and
 * `line-height: 150%` as a 150pt pitch, because `storedLeadingNumber` is a bare `parseFloat` with
 * no unit vocabulary of its own. That is the whole defect: the two halves of one paste disagreed
 * about what a CSS pixel is, and only the `font-size` half was converting.
 *
 * The result is normalised against the ratio band (`isLeadingRatio`) so it can never be re-read as
 * the other kind of value: a ratio above the band is resolved to an absolute pt length, and an
 * absolute length that would land inside it is lifted clear — the same guard
 * `computeNarrowLeadingPt` (`type-panel/helpers/leading-presets.ts`) applies to the values the
 * Type panel writes.
 *
 * Returns `undefined` for `normal`, the CSS-wide keywords, and anything unparseable — all of which
 * mean "no explicit leading", i.e. Metric. `undefined` (rather than a computed default) is what
 * lets the caller fall back to the destination frame's own leading.
 */
export function cssLineHeightToStoredLeading(
  value: string | undefined | null,
  pointSize: number | undefined
): string | undefined {
  const raw = (value ?? '').trim().toLowerCase()

  if (raw === '' || CSS_LINE_HEIGHT_KEYWORDS.has(raw)) return undefined

  const match = raw.match(/^(-?[\d.]+)(px|pt|%|em|rem)?$/)

  if (!match) return undefined

  const n = parseFloat(match[1]!)

  // Zero and negative line-heights are not expressible in this model (leading is a row pitch), and
  // a NaN would sail straight through `parseFloat` on the read side as a silent Metric.
  if (!isFinite(n) || n <= 0) return undefined

  const unit = match[2]
  // `%`, `em`/`rem` and the unitless form are all size-relative, which is exactly what the stored
  // ratio band means — so they convert without needing the point size at all.
  const ratio =
    unit === '%' ? n / 100 : unit === 'em' || unit === 'rem' ? n : unit == null ? n : undefined
  const usablePointSize =
    typeof pointSize === 'number' && isFinite(pointSize) && pointSize > 0
      ? pointSize
      : DEFAULT_POINT_SIZE

  if (ratio != null) {
    // A ratio outside the band would be re-read as an absolute pt length, so resolve it to one
    // against the run's own size rather than letting it flip meaning.
    return isLeadingRatio(ratio)
      ? String(roundLeading(ratio, 4))
      : String(roundLeading(ratio * usablePointSize, 1))
  }

  const absolutePt = roundLeading(unit === 'px' ? n * FOREIGN_CSS_PX_TO_PT : n, 1)

  // Mirror of the branch above: an absolute length inside the band would be re-read as a
  // multiplier, turning "4pt of leading" into "4× the point size".
  return String(Math.max(absolutePt, LEADING_RATIO_BAND_MAX + 0.1))
}
